import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Mail, Building2, ArrowLeft, Archive, Loader2, AlertTriangle, ArrowUpDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const NIVEL_BADGE = {
  informativo: 'bg-slate-100 text-slate-700',
  atencao: 'bg-amber-100 text-amber-700',
  urgente: 'bg-red-100 text-red-700',
};

export default function DetArquivadasPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [sortAsc, setSortAsc] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    axios.get(`${API}/api/companies`, auth()).then(r => {
      setCompanies(r.data || []); if (r.data?.length) setCompanyId(r.data[0].id);
    });
  }, []);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const params = { company_id: companyId, arquivada: 'true', limit: 200, sort_order: sortAsc ? 'asc' : 'desc' };
      if (filtro === 'nao_lidas') params.lida = false;
      else if (filtro === 'urgentes') { params.nivel = 'urgente'; params.lida = false; }
      const r = await axios.get(`${API}/api/det/mensagens`, { ...auth(), params });
      setMsgs(r.data || []);
    } finally { setLoading(false); }
  }, [companyId, filtro, sortAsc]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const abrir = async (m) => {
    const r = await axios.get(`${API}/api/det/mensagens/${m.id}`, auth());
    setOpen(r.data);
  };

  return (
    <div className="p-6 space-y-4 max-w-7xl mx-auto" data-testid="det-arquivadas-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Archive className="h-7 w-7 text-indigo-600" />
            DET — Mensagens Arquivadas
          </h1>
          <p className="text-sm text-slate-500">Mensagens arquivadas do Ministério do Trabalho</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={companyId || ''} onValueChange={setCompanyId}>
            <SelectTrigger className="w-64" data-testid="det-arquivadas-company-select">
              <Building2 className="h-4 w-4 mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name || c.razao_social}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => navigate('/dp-folha/det')} data-testid="det-voltar-btn">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Archive className="h-5 w-5 text-indigo-600" />Mensagens Arquivadas</CardTitle>
            <CardDescription>Mensagens que foram arquivadas</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filtro} onValueChange={setFiltro}>
              <SelectTrigger className="w-44" data-testid="det-arquivadas-filtro"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Todas</SelectItem>
                <SelectItem value="nao_lidas">Não lidas</SelectItem>
                <SelectItem value="urgentes">Urgentes</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="ghost" size="icon" onClick={() => setSortAsc(p => !p)} data-testid="det-arquivadas-sort-btn" title={sortAsc ? 'Mais antigas primeiro' : 'Mais recentes primeiro'}>
              <ArrowUpDown className={`h-4 w-4 ${sortAsc ? 'text-indigo-600' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-2">
              {msgs.length === 0 && <div className="text-center text-slate-400 py-8">Nenhuma mensagem arquivada</div>}
              {msgs.map(m => (
                <button key={m.id}
                  onClick={() => abrir(m)}
                  data-testid={`det-arquivadas-msg-${m.id}`}
                  className={`w-full text-left border rounded-lg p-3 hover:bg-slate-50 ${m.lida ? 'opacity-60' : 'bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm flex items-center gap-2">
                      {!m.lida && <span className="h-2 w-2 rounded-full bg-blue-500" />}
                      {m.assunto}
                    </div>
                    <Badge className={NIVEL_BADGE[m.nivel] || ''}>{m.nivel}</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">{m.remetente} · Prazo: {m.prazo_resposta || '—'} · {(m.criado_em || '').slice(0, 16).replace('T', ' ')}</div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!open} onOpenChange={v => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="det-arquivadas-msg-dialog">
          {open && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {open.nivel === 'urgente' && <AlertTriangle className="h-5 w-5 text-red-600" />}
                  {open.assunto}
                </DialogTitle>
                <DialogDescription>{open.remetente} · {(open.data_envio || '').slice(0, 16).replace('T', ' ')} · Prazo: {open.prazo_resposta || '—'}</DialogDescription>
              </DialogHeader>
              <div className="text-sm whitespace-pre-wrap py-2">{open.corpo}</div>
              <div className="flex gap-2 justify-end mt-3">
                <Button variant="outline" onClick={() => setOpen(null)}>Fechar</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}